/**
 * Planos de assinatura do perfil.
 *
 * So aparece quando o estabelecimento tem plano publico ativo — a API ja
 * devolve apenas esses, e por isso a lista nao e refiltrada aqui: um filtro
 * novo que esquecesse `publicVisible` publicaria numa pagina aberta o plano
 * que o dono marcou como privado.
 *
 * Esta secao e a dona da acao de assinar. O convite de conta, logo abaixo,
 * voltou a dizer so "Entrar": dois botoes de assinar lado a lado era a
 * repeticao que a pagina passou tres rodadas tirando.
 *
 * Plano com imagem mostra a imagem: e o que o dono cadastrou para vender o
 * plano, e deixar de exibir seria descartar o material dele. Sem imagem, a
 * moldura continua ali com o icone de foto — o mesmo aviso que as vitrines de
 * servico e produto dao, e que evita o card ficar com um vazio no meio.
 *
 * Assinar exige conta, entao o destino depende da sessao: quem esta dentro vai
 * para a aba de assinaturas; quem nao esta passa pelo login e volta para lá.
 */

import Link from "next/link";
import { FiCheck, FiImage, FiPercent, FiRepeat } from "react-icons/fi";
import { resolveImageUrl } from "../utils";
import type { CustomerMembershipPlan } from "../../../services/customerMemberships";
import { formatBRL } from "../../../lib/currency";
import { PreviewCarousel } from "./PreviewCarousel";

interface ProfileMembershipPreviewProps {
  plans: CustomerMembershipPlan[];
  /** Aba de assinaturas na area do cliente, ja com o login pelo meio se preciso. */
  membershipHref: string;
  isLoggedIn: boolean;
}

/** "R$ 99,90 /mês" — ou por dia, quando o plano cobra assim. */
function formatarMensalidade(plan: CustomerMembershipPlan) {
  const valor = formatBRL(Number(plan.monthlyPriceCents || 0) / 100);
  return { valor, periodo: plan.intervalUnit === "day" ? "/dia" : "/mês" };
}

/** Vantagens que o proprio plano declara. Nada e inferido. */
function resolverVantagens(plan: CustomerMembershipPlan): string[] {
  const vantagens: string[] = [];

  const desconto = Number(plan.productDiscountPercent || 0);
  if (desconto > 0) {
    vantagens.push(`${desconto}% de desconto em produtos`);
  }

  const dias = plan.coveredWeekdayLabels?.filter(Boolean) || [];
  if (dias.length > 0 && dias.length < 7) {
    vantagens.push(`Vale ${dias.length === 1 ? "na" : "nas"} ${dias.join(", ")}`);
  }

  const servicos = plan.serviceIds?.length || 0;
  if (servicos > 0) {
    vantagens.push(`${servicos} ${servicos === 1 ? "serviço" : "serviços"} incluídos`);
  }

  return vantagens.slice(0, 3);
}

export function ProfileMembershipPreview({
  plans,
  membershipHref,
  isLoggedIn,
}: ProfileMembershipPreviewProps) {
  if (plans.length === 0) return null;

  const rotuloAcao = isLoggedIn ? "Assinar" : "Entrar e assinar";

  return (
    <section
      // OFR-21: o link de oferta do WhatsApp abre o perfil nesta seção.
      id="assinaturas"
      aria-label="Planos de assinatura"
      data-testid="profile-membership-preview"
      className="rounded-2xl bg-white px-3 py-4 shadow-sm"
    >
      <div className="mb-1 px-1">
        <h2 className="text-base font-bold text-[#233055]">Planos de assinatura</h2>
        <p className="mt-0.5 text-sm text-slate-600">
          Pague por mês e use quando quiser.
        </p>
      </div>

      <PreviewCarousel
        items={plans}
        getKey={(plan) => plan.id}
        renderItem={(plan) => {
          const { valor, periodo } = formatarMensalidade(plan);
          const vantagens = resolverVantagens(plan);
          const descricao = String(plan.description || "").trim();
          const imageSrc = plan.imageUrl ? resolveImageUrl(plan.imageUrl) : "";

          return (
            <Link
              href={membershipHref}
              data-testid={`profile-plan-card-${plan.id}`}
              aria-label={`Plano ${plan.name}, ${valor} ${periodo === "/dia" ? "por dia" : "por mês"}. ${rotuloAcao}.`}
              className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:border-primary-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
            >
              {/**
               * A moldura existe sempre, com foto ou sem. Antes o plano sem
               * imagem levava so uma estrela pequena no topo, e o card ficava
               * com um vazio no meio ate o botao — a altura vem do carrossel,
               * que iguala todos os cards. Com a moldura ocupando o topo, o
               * card sem foto tem a mesma estrutura do card com foto, e o
               * icone diz o que falta em vez de deixar um buraco.
               */}
              <div
                className="relative aspect-[16/9] w-full shrink-0 overflow-hidden bg-slate-100"
                aria-hidden="true"
                data-testid={
                  imageSrc
                    ? `profile-plan-image-${plan.id}`
                    : `profile-plan-image-empty-${plan.id}`
                }
              >
                {imageSrc ? (
                  <>
                    {/* Fundo desfocado preenche a moldura sem recortar a foto. */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={imageSrc}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 h-full w-full scale-125 object-cover opacity-45 blur-2xl"
                    />
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={imageSrc}
                      alt=""
                      className="relative z-10 h-full w-full object-contain"
                    />
                  </>
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-slate-400">
                    <FiImage className="h-8 w-8" aria-hidden="true" />
                  </div>
                )}
              </div>

              <div aria-hidden="true" className="flex-1 p-4">
                <p className="line-clamp-2 text-base font-bold leading-tight text-[#233055]">
                  {plan.name}
                </p>

                <p className="mt-1.5">
                  <span className="text-xl font-black leading-none text-primary-700">
                    {valor}
                  </span>
                  <span className="ml-1 text-sm font-semibold text-slate-500">
                    {periodo}
                  </span>
                </p>

                {descricao ? (
                  <p className="mt-2 line-clamp-2 text-sm text-slate-600">{descricao}</p>
                ) : null}

                {vantagens.length > 0 ? (
                  <ul className="mt-2.5 space-y-1">
                    {vantagens.map((vantagem) => (
                      <li
                        key={vantagem}
                        className="flex items-start gap-1.5 text-xs font-medium text-slate-600"
                      >
                        {vantagem.startsWith("Vale") ? (
                          <FiRepeat
                            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-600"
                            aria-hidden="true"
                          />
                        ) : vantagem.includes("desconto") ? (
                          <FiPercent
                            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-600"
                            aria-hidden="true"
                          />
                        ) : (
                          <FiCheck
                            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-600"
                            aria-hidden="true"
                          />
                        )}
                        <span>{vantagem}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              <span
                aria-hidden="true"
                className="mx-4 mb-4 mt-3 inline-flex min-h-[40px] items-center justify-center rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 px-4 text-sm font-bold leading-none text-white"
              >
                {rotuloAcao}
              </span>
            </Link>
          );
        }}
        ariaLabel="Planos em destaque"
        previousLabel="Ver plano anterior"
        nextLabel="Ver próximo plano"
        testId="profile-plans-carousel"
      />
    </section>
  );
}
